import React, { useState, useEffect } from 'react'
import { Table, Spin, message, Layout } from 'antd'
import { getTotalOutstandingCommission } from 'domains/outstandingInvoices/api'
import styled from 'styled-components'

const { Content } = Layout

// Helper function to format numbers with commas
const formatCurrency = (value) => {
  const numValue = Math.round(parseFloat(value))
  if (isNaN(numValue)) return '$0'
  return `$${numValue.toLocaleString()}`
}

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th:first-child,
  .ant-table-tbody > tr > td:first-child {
    position: sticky;
    left: 0;
    z-index: 1;
    background: #fff;
  }
  
  .ant-table-thead > tr > th:first-child {
    z-index: 2;
  }

  .ant-table-tbody > tr > td {
    height: 3.2em !important;
    max-height: 3.2em !important;
    line-height: 1.6 !important;
    overflow: hidden !important;
    word-wrap: break-word !important;
    white-space: normal !important;
    vertical-align: top !important;
    padding: 8px 16px !important;
  }

  .ant-table-tbody > tr > td > div {
    height: 3.2em !important;
    max-height: 3.2em !important;
    line-height: 1.6 !important;
    overflow: hidden !important;
    word-wrap: break-word !important;
    white-space: normal !important;
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
  }

  .ant-table-thead > tr > th {
    height: 3.2em !important;
    max-height: 3.2em !important;
    line-height: 1.6 !important;
    overflow: hidden !important;
    word-wrap: break-word !important;
    white-space: normal !important;
    vertical-align: top !important;
    padding: 8px 16px !important;
  }
`

const Wrapper = styled(Content)`
  padding: 20px;
  min-height: calc(100vh - 64px);
`

const TotalOutstandingCommission = () => {
  const [data, setData] = useState([])
  const [columns, setColumns] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const response = await getTotalOutstandingCommission()
      if (response && response.data && response.data.records) {
        const records = response.data.records
        if (records.length > 0) {
          // Extract all unique months from all records in the order they appear
          const allMonths = []
          const seenMonths = new Set()

          records.forEach(record => {
            record.months.forEach(monthData => {
              if (!seenMonths.has(monthData.month)) {
                allMonths.push(monthData.month)
                seenMonths.add(monthData.month)
              }
            })
          })

          // Create columns: first column for network, then one for each month, then total
          const dynamicColumns = [
            {
              title: 'Network',
              dataIndex: 'network',
              key: 'network',
              width: 200,
              fixed: 'left',
            },
            ...allMonths.map(month => ({
              title: month,
              dataIndex: month,
              key: month,
              width: '100%',
              render: (value) => {
                if (value === undefined || value === null) return '-'
                return value // Value is already formatted with $ and commas
              },
            })),
            {
              title: 'Total',
              dataIndex: 'total',
              key: 'total',
              width: '100%',
              fixed: 'right',
              render: (value) => {
                return value // Value is already formatted with $ and commas
              },
            }
          ]

          // Transform the data: flatten each record to have network + month values + total
          const transformedData = records.map(record => {
            const flatRecord = { network: record.network }
            let rowTotal = 0

            // Create a map of month -> value for this record
            const monthValues = {}
            record.months.forEach(monthData => {
              // Format the value with $ and commas here
              const numValue = Math.round(parseFloat(monthData.value))
              if (!isNaN(numValue)) {
                monthValues[monthData.month] = formatCurrency(numValue)
                rowTotal += numValue
              } else {
                monthValues[monthData.month] = monthData.value
              }
            })

            // Add each month as a separate property
            allMonths.forEach(month => {
              flatRecord[month] = monthValues[month] || null
            })

            // Add the total for this row
            flatRecord.total = formatCurrency(rowTotal)

            return flatRecord
          })

          setColumns(dynamicColumns)
          setData(transformedData)
        }
      }
    } catch (error) {
      message.error('Failed to fetch total outstanding commission data')
    } finally {
      setLoading(false)
    }
  }

  const handlePaginationChange = (page, size) => {
    setCurrentPage(page)
    setPageSize(size)
  }

  const handlePageSizeChange = (current, size) => {
    setCurrentPage(1) // Reset to first page when page size changes
    setPageSize(size)
  }

  // Calculate totals for each month column
  const calculateTotals = () => {
    if (data.length === 0 || columns.length === 0) return {}

    const totals = {}
    columns.forEach((column, index) => {
      if (index > 0 && column.dataIndex !== 'total') { // Skip the first column (network) and total column for now
        const total = data.reduce((sum, record) => {
          const value = record[column.dataIndex]
          if (value && typeof value === 'string' && value.startsWith('$')) {
            // Parse the formatted string back to number
            const numValue = Math.round(parseFloat(value.replace('$', '').replace(/,/g, '')))
            return sum + (isNaN(numValue) ? 0 : numValue)
          }
          return sum
        }, 0)
        // Format the total with $ and commas
        totals[column.dataIndex] = formatCurrency(total)
      }
    })

    // Calculate the total column sum (grand total)
    const grandTotalSum = Object.values(totals).reduce((sum, value) => {
      if (typeof value === 'string' && value.startsWith('$')) {
        const numValue = Math.round(parseFloat(value.replace('$', '').replace(/,/g, '')))
        return sum + (isNaN(numValue) ? 0 : numValue)
      }
      return sum
    }, 0)

    totals.total = formatCurrency(grandTotalSum)

    return totals
  }

  const totals = calculateTotals()
  // Extract grand total for display
  const grandTotalForDisplay = totals.total ? Math.round(parseFloat(totals.total.replace('$', '').replace(/,/g, ''))) : 0

  return (
    <Wrapper>
      <h1>Total Outstanding Commission</h1>
      {data.length > 0 && (
        <h4>Grand Total: {formatCurrency(grandTotalForDisplay)}</h4>
      )}
      <Spin spinning={loading}>
        <StyledTable
          columns={columns}
          dataSource={data}
          rowKey={(record, index) => record.network || index}
          scroll={{ x: Math.max(1500, (columns.length - 1) * 120 + 200 + 150), y: '100%' }}
          pagination={{
            total: data.length,
            pageSize: pageSize,
            current: currentPage,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
            onChange: handlePaginationChange,
            onShowSizeChange: handlePageSizeChange,
          }}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}>
                <strong>Total</strong>
              </Table.Summary.Cell>
              {columns.slice(1).map((column, index) => (
                <Table.Summary.Cell key={column.key} index={index + 1}>
                  <strong>{totals[column.dataIndex] || '$0'}</strong>
                </Table.Summary.Cell>
              ))}
            </Table.Summary.Row>
          )}
        />
      </Spin>
    </Wrapper>
  )
}

export default TotalOutstandingCommission